import { posthog } from 'posthog-js';

const CONTAINS_DIGIT = /\d/;
const CONTAINS_CHARACTER = /[a-z]/;

const getSanitizedPathname = (pathname: string) => {
  return pathname
    .split('/')
    .map((part) => {
      const lowerCasePart = part.toLowerCase();

      if (
        CONTAINS_DIGIT.test(lowerCasePart) &&
        CONTAINS_CHARACTER.test(lowerCasePart)
      ) {
        // UUID and ULID lengths
        // This is a convenience more than a necessity in the analytics
        if (lowerCasePart.length === 36 || lowerCasePart.length === 26) {
          return ':id';
        }

        return ':param';
      }

      return part;
    })
    .join('/');
};

function redactPathFromString(
  value: string,
  pathname: string,
  sanitizedPathname: string,
): string {
  if (value.includes(pathname)) {
    return value.replace(pathname, sanitizedPathname);
  }

  return value;
}

function redactObject(
  objectToRedact: Record<string, any>,
  pathname: string,
  sanitizedPathname: string,
): Record<string, any> {
  return Object.entries(objectToRedact).reduce(
    (acc: Record<string, any>, [key, value]) => {
      let redactedValue = value;

      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          redactedValue = value.map((item) =>
            typeof item === 'object'
              ? redactObject(item, pathname, sanitizedPathname)
              : typeof item === 'string'
                ? redactPathFromString(item, pathname, sanitizedPathname)
                : item,
          );
        } else {
          redactedValue =
            typeof value === 'object'
              ? redactObject(value, pathname, sanitizedPathname)
              : typeof value === 'string'
                ? redactPathFromString(value, pathname, sanitizedPathname)
                : value;
        }
      }

      acc[redactPathFromString(key, pathname, sanitizedPathname)] =
        redactedValue;

      return acc;
    },
    {},
  );
}

export const initAnalytics = () => {
  return posthog.init(APP_CONFIG.posthog.apiKey, {
    api_host: APP_CONFIG.posthog.apiHost,
    person_profiles: 'always',
    disable_persistence: true,
    mask_all_text: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
    },
    loaded: (client) => {
      if (!APP_CONFIG.posthog.enabled) {
        client.opt_out_capturing();
      } else {
        // Skip the $opt_in event — it is billable and fires on every init.
        // Called on the `posthog` singleton rather than the `client` argument:
        // both are the same instance here, but `loaded` types its argument with
        // @posthog/types' PostHog interface, which still declares
        // `opt_in_capturing()` without the options parameter that posthog-js
        // implements. Revisit once that declaration is fixed upstream.
        posthog.opt_in_capturing({ captureEventName: false });
      }
    },
    before_send: (e) => {
      if (!e) {
        return null;
      }

      const { pathname } = window.location;
      const sanitizedPathname = getSanitizedPathname(pathname);

      const redactedProperties = redactObject(
        e.properties || {},
        pathname,
        sanitizedPathname,
      );
      e.properties = redactedProperties;

      const redactedSet = redactObject(
        e.$set || {},
        pathname,
        sanitizedPathname,
      );
      e.$set = redactedSet;

      const redactedSetOnce = redactObject(
        e.$set_once || {},
        pathname,
        sanitizedPathname,
      );
      e.$set_once = redactedSetOnce;

      return e;
    },
  });
};
